import { pull } from 'lodash';
import { reactive } from 'vue';
import { parseInvoiceFile } from '@/general/converters/mediaFileConverters';
import InvoiceFile from '@/general/models/InvoiceFile';
import pushService from '@/general/services/PushService';
import serviceEvents, { MediaRemoved } from '@/shared/services/events/serviceEvents';
import { removeById } from '@/shared/utils';
import { present } from '@/shared/utils/typescript';
import PaymentRequestClient from '../client/PaymentRequestClient';
import PaymentStatus from '../constants/PaymentStatus';
import DossierContext from '../models/DossierContext';
import { PaymentRequestId } from '../models/PaymentRequest';
import PaymentRequestContext from '../models/PaymentRequestContext';
import { PaymentRequestServiceData } from '../models/PaymentRequestData';

class PaymentRequestService {
  private context: PaymentRequestContext | null = null;

  async createContext(dossierContext: DossierContext): Promise<void> {
    const dossierId = dossierContext.dossier.id;
    const requests = await PaymentRequestClient.loadAll(dossierId);
    this.context?.free();
    this.context = new PaymentRequestContext(requests, dossierContext);
    this.context.pushUnbinders.push(
      serviceEvents.on(MediaRemoved, e => removeById(this.context!.files, e.mediaFile.id)),
    );
  }

  getContext(): PaymentRequestContext {
    return present(this.context);
  }

  unloadCurrent(): void {
    this.context?.free();
    this.context = null;
  }

  async loadInvoiceFiles(context: PaymentRequestContext): Promise<void> {
    const dossierId = context.dossierContext.dossier.id;
    context.files = await PaymentRequestClient.getInvoiceFiles(dossierId);
  }

  async create(data: PaymentRequestServiceData, context: PaymentRequestContext): Promise<void> {
    const clientData = {
      ...data,
      attachment: data.attachment?.contentId ?? null,
    };
    const request = await PaymentRequestClient.create(clientData, context.dossierContext.dossier.id);
    context.requests.push(request);
    context.dossierContext.dossier.paymentStatus = PaymentStatus.OPEN;
  }

  async remove(id: PaymentRequestId, context: PaymentRequestContext): Promise<void> {
    await PaymentRequestClient.remove(id, context.dossierContext.dossier.id);
    context.removeRequest(id);
  }

  addInvoiceFile(file: File, context: PaymentRequestContext): [InvoiceFile, Promise<void>] {
    const dossierId = context.dossierContext.dossier.id;
    const placeholder = reactive(new InvoiceFile()) as InvoiceFile;
    context.files.push(placeholder);

    const idPromise = PaymentRequestClient.uploadAttachment(file, context.dossierContext.dossier.id);

    const uploadPromise = pushService.sequence(`dossiers-${dossierId}/invoice-media-uploaded`, async (event, resolve) => {
      const newFile = present(parseInvoiceFile(event.media));
      if (newFile.contentId === await idPromise) {
        placeholder.updateWith(newFile);
        resolve();
      }
    });

    uploadPromise.catch(() => pull(context.files, placeholder));

    return [placeholder, uploadPromise];
  }
}

export default reactive(new PaymentRequestService());